import React, { useRef, useState, useEffect, useCallback } from 'react';
import styles from './AudioControls.module.css';

const AudioControls = ({
  // 进度相关
  currentTime = 0,
  duration = 0,
  onProgressChange,
  formatTime,
  
  // 播放控制
  isPlaying = false,
  onPlayPause,
  onPrevSegment,
  onNextSegment,
  disableTransport = false,
  
  // 速度控制
  playbackSpeed = 1,
  speedOptions = [0.75, 1, 1.25, 1.5, 1.75, 2],
  onSpeedChange,
  
  // 左侧额外控制 (可选)
  leftControls = null,
  
  // 样式类名前缀 (用于区分不同组件的样式)
  classPrefix = ''
}) => {
  const [showSpeedSettings, setShowSpeedSettings] = useState(false);
  const speedSettingsRef = useRef(null);

  const handleSpeedToggle = useCallback(() => {
    setShowSpeedSettings((prev) => !prev);
  }, []);

  const handleSpeedSelect = useCallback((speed) => {
    if (onSpeedChange) {
      onSpeedChange(speed);
    }
    setShowSpeedSettings(false);
  }, [onSpeedChange]);

  useEffect(() => {
    if (!showSpeedSettings) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (speedSettingsRef.current && !speedSettingsRef.current.contains(event.target)) {
        setShowSpeedSettings(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowSpeedSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSpeedSettings]);

  // 计算进度百分比
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // 动态生成进度条背景样式
  const progressBarStyle = {
    background: `linear-gradient(to right, rgb(var(--theme-50)) 0%, rgb(var(--theme-50)) ${progress}%, rgba(148, 163, 184, 0.35) ${progress}%, rgba(148, 163, 184, 0.35) 100%)`
  };

  return (
    <div className={styles.controlsWrapper}>
      {/* 进度条 */}
      <div className={styles.progressSection}>
        <input
          type="range"
          className={styles.progressBar}
          min={0}
          max={duration || 0}
          step={0.1}
          value={Math.min(currentTime, duration || 0)}
          onChange={onProgressChange}
          style={progressBarStyle}
        />
        <div className={styles.progressBarInner}>
          <span className={styles.timeMark}>{formatTime ? formatTime(currentTime) : '00:00'}</span>
          <span className={styles.timeMark}>{formatTime ? formatTime(duration) : '00:00'}</span>
        </div>
      </div>

      {/* 控制按钮 */}
      <div className={styles.bottomControlsInner}>
        {/* 左侧控制 (如播放模式设置) 或占位符 */}
        {leftControls ? (
          <div className={styles.leftControlsSlot}>{leftControls}</div>
        ) : (
          <div className={styles.transportSpacer} />
        )}

        {/* 播放控制按钮 */}
        <div className={styles.transportCenter}>
          <button
            type="button"
            className={styles.transportBtn}
            onClick={onPrevSegment}
            aria-label="播放上一句"
            disabled={disableTransport}
          >
            <span className={`${styles.transportIcon} ${styles.iconPrev}`} aria-hidden="true" />
          </button>

          <button
            type="button"
            className={`${styles.transportBtn} ${styles.transportBtnPrimary}`}
            onClick={onPlayPause}
            aria-label={isPlaying ? '暂停播放' : '开始播放'}
          >
            <span className={styles.transportIcon}>
              {isPlaying ? (
                <>
                  <span className={styles.iconPause} />
                  <span className={styles.iconPause} />
                </>
              ) : (
                <span className={styles.iconPlay} />
              )}
            </span>
          </button>

          <button
            type="button"
            className={styles.transportBtn}
            onClick={onNextSegment}
            aria-label="播放下一句"
            disabled={disableTransport}
          >
            <span className={`${styles.transportIcon} ${styles.iconNext}`} aria-hidden="true" />
          </button>
        </div>

        {/* 速度设置 */}
        <div className={styles.speedButtonWrapper} ref={speedSettingsRef}>
          <button
            type="button"
            className={styles.speedBtn}
            onClick={handleSpeedToggle}
            aria-label="调整播放速度"
            aria-haspopup="menu"
            aria-expanded={showSpeedSettings}
          >
            {playbackSpeed.toFixed(2)}x
          </button>

          {showSpeedSettings && (
            <div className={styles.speedSettingsPanel} role="menu" aria-label="播放速度设置">
              {speedOptions.map((speed) => {
                const label = `${speed.toFixed(2)}x`;
                const active = playbackSpeed === speed;
                return (
                  <button
                    key={label}
                    type="button"
                    className={`${styles.speedOptionBtn} ${active ? styles.speedOptionActive : ''}`}
                    onClick={() => handleSpeedSelect(speed)}
                    role="menuitemradio"
                    aria-checked={active}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioControls;

